import React, { useRef, useEffect, useState, useCallback } from 'react';
import { getQuoteFileStem } from '@shared/quote-naming';
import { useOfferStore, useQuoteTotals } from '@/lib/store';
import { normalizeVatRate } from '@/lib/quote-calculations';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ZoomIn,
  ZoomOut,
  RotateCw,
  FileText,
  Loader2,
  Download,
  Printer,
  X
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Debounce hook for delayed PDF regeneration
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

interface LivePDFPreviewProps {
  onClose?: () => void;
}

export function LivePDFPreview({ onClose }: LivePDFPreviewProps) {
  const quoteHeader = useOfferStore(s => s.quoteHeader);
  const quoteItems = useOfferStore(s => s.quoteItems);
  const gewerkOrder = useOfferStore(s => s.gewerkOrder);
  const discountAmount = useOfferStore(s => s.discountAmount);
  const discountType = useOfferStore(s => s.discountType);
  const discountApplicationMode = useOfferStore(s => s.discountApplicationMode);
  const { totalNet: netTotal } = useQuoteTotals();
  const vatRate = normalizeVatRate(quoteHeader.mwstSatz);

  // PDF state
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageCount, setPageCount] = useState(0);

  // View controls
  const [zoom, setZoom] = useState(125);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Create a hash of the current data to detect changes
  const dataHash = JSON.stringify({
    header: quoteHeader,
    itemCount: quoteItems.length,
    // Positionsart (Bedarf/Alternativ) ändert PDF-Labels + Info-Summen, aber nicht
    // zwingend netTotal — muss deshalb selbst in den Hash. Gleiches gilt für OZ +
    // Untertitel (F2): "Neu nummerieren" ändert nur positionNumber.
    items: quoteItems.map(i => ({ id: i.id, menge: i.menge, preis: i.preis, beschreibung: i.beschreibung?.slice(0, 50), b: i.isBedarfsposition ? 1 : 0, a: i.isAlternativposition ? 1 : 0, oz: i.positionNumber || '', ut: i.untergewerkName || '' })),
    gewerkOrder,
    netTotal,
    discountAmount,
    discountType,
    discountApplicationMode,
    vatRate,
  });

  // Debounce data changes (regenerate PDF 1.5s after last change)
  const debouncedDataHash = useDebounce(dataHash, 1500);

  // Generate PDF from server
  const generatePDF = useCallback(async () => {
    const activeItems = quoteItems.filter(i => i.menge > 0);
    if (activeItems.length === 0) {
      setPdfUrl(null);
      setPageCount(0);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/generate-pdf-accurate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          header: quoteHeader,
          items: activeItems,
          gewerkOrder,
          options: {
            aiOptimization: false,
            discountAmount,
            discountType,
            discountApplicationMode,
            vatRate,
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const blob = await response.blob();

      // Revoke old URL to prevent memory leaks
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }

      const newUrl = URL.createObjectURL(blob);
      setPdfUrl(newUrl);

      // Estimate page count
      const estimatedPages = Math.max(1, Math.ceil(activeItems.length / 15));
      setPageCount(estimatedPages);

    } catch (err) {
      console.error('PDF generation failed:', err);
      setError(err instanceof Error ? err.message : 'Fehler');
    } finally {
      setIsLoading(false);
    }
  }, [quoteHeader, quoteItems, gewerkOrder, pdfUrl, discountAmount, discountType, discountApplicationMode, vatRate]);

  // Regenerate PDF when data changes (debounced)
  useEffect(() => {
    generatePDF();
  }, [debouncedDataHash]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, []);

  const pdfViewerSrc = React.useMemo(() => {
    if (!pdfUrl) {
      return '';
    }
    return `${pdfUrl}#toolbar=1&navpanes=0&zoom=${zoom}`;
  }, [pdfUrl, zoom]);

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `${getQuoteFileStem({ header: quoteHeader }, 'Angebot Vorschau')}.pdf`;
      link.click();
    }
  };

  const handlePrint = () => {
    if (pdfUrl) {
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      }
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-800 rounded-lg overflow-hidden shadow-xl relative">
      {/* Minimal Header - no title, just status and close */}
      <div className="flex items-center justify-between px-2 py-1 bg-gray-900/80 absolute top-0 left-0 right-0 z-10">
        <div className="flex items-center gap-2 min-w-0">
          {isLoading ? (
            <Loader2 className="h-3 w-3 animate-spin text-blue-400 flex-shrink-0" />
          ) : pageCount > 0 ? (
            <Badge variant="secondary" className="text-[10px] bg-gray-700 text-gray-300 flex-shrink-0">
              ~{pageCount} Seiten
            </Badge>
          ) : null}
        </div>

        {onClose && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0 text-white hover:text-white hover:bg-red-600 rounded-full"
            title="Vorschau schließen"
          >
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* PDF Viewer Area - full height with padding for controls */}
      <div
        ref={containerRef}
        className="flex-1 overflow-auto bg-gray-700 pt-8"
      >
        {isLoading && !pdfUrl ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-white">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-blue-400" />
              <p className="text-xs text-gray-300">Generiere PDF...</p>
            </div>
          </div>
        ) : pdfUrl ? (
          <iframe
            ref={iframeRef}
            src={pdfViewerSrc}
            className="w-full h-full border-0"
            title="Vorschau"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-400 px-4">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-xs">Keine Positionen</p>
            </div>
          </div>
        )}
      </div>

      {/* Compact Footer with Actions */}
      <div className="flex items-center justify-between px-2 py-1.5 bg-gray-900 border-t border-gray-700">
        {/* Zoom Controls */}
        <div className="flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setZoom(z => Math.max(50, z - 25))}
            className="h-6 w-6 p-0 text-gray-400 hover:text-white hover:bg-gray-700"
            disabled={zoom <= 50}
          >
            <ZoomOut className="h-3 w-3" />
          </Button>
          <span className="text-[10px] text-gray-400 w-8 text-center">{zoom}%</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setZoom(z => Math.min(200, z + 25))}
            className="h-6 w-6 p-0 text-gray-400 hover:text-white hover:bg-gray-700"
            disabled={zoom >= 200}
          >
            <ZoomIn className="h-3 w-3" />
          </Button>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-0.5">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={generatePDF}
                  className="h-6 w-6 p-0 text-gray-400 hover:text-white hover:bg-gray-700"
                  disabled={isLoading}
                >
                  <RotateCw className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">Aktualisieren</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handlePrint}
                  className="h-6 w-6 p-0 text-gray-400 hover:text-white hover:bg-gray-700"
                  disabled={!pdfUrl}
                >
                  <Printer className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">Drucken</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDownload}
                  className="h-6 w-6 p-0 text-gray-400 hover:text-white hover:bg-gray-700"
                  disabled={!pdfUrl}
                >
                  <Download className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">Download</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Loading overlay when regenerating */}
      {isLoading && pdfUrl && (
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center pointer-events-none rounded-lg">
          <div className="bg-gray-900 rounded-lg px-3 py-2 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
            <span className="text-xs text-white">Aktualisiere...</span>
          </div>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="absolute bottom-12 left-2 right-2">
          <Badge variant="destructive" className="text-[10px] w-full justify-center">
            {error}
          </Badge>
        </div>
      )}
    </div>
  );
}

export default LivePDFPreview;
